import { z } from "zod";

/**
 * Departman kataloğu (CLOS-190 `[052]`).
 *
 * ### Kartın en sert sınırı: KATALOG, ATAMA DEĞİL
 *
 * *"Kullanıcı–departman atamasının sahibi CLOS-189'dur; CLOS-190 atama veya çıkarma
 * command'ı üretmez."* Bu dosyada bir atama şeması aramayın: yok ve olmayacak. Departman
 * bir listedir; kimin hangi departmanda olduğu üyeliğin bilgisidir.
 *
 * ### Departman bir YETKİ KAYNAĞI DEĞİL
 *
 * Kart ayrıca: *"departman organizasyon rolü, permission veya sözleşme kayıt erişiminin
 * OTOMATİK KAYNAĞI DEĞİLDİR."* Departmanı yetkiye bağlamak cazip ve yanlış olurdu — o gün
 * "Hukuk departmanına ekle" demek sessizce yetki vermek anlamına gelirdi ve yetkinin
 * nereden geldiği bir daha hiç okunamazdı.
 */

/**
 * Sürümlü konfigürasyon (kartın AÇIK KARARI: *"departman sayısı, ad uzunluğu/karakter
 * politikası ve filtreleme davranışı sürümlü konfigürasyondur"*).
 *
 * Değerler `config/department.json`'da; burada yalnız o dosyanın ŞEKLİ. Doğrulanıyor:
 * elle yazılan bir JSON'daki yazım hatası sessizce sıfır uzunluk sınırı ya da sıfır
 * departman limiti demek olurdu ve arızayı ilk kullanıcı keşfederdi.
 */
export const departmentConfigSchema = z.object({
  /** Organizasyon başına AKTİF departman üst sınırı. Arşivliler sayılmaz. */
  maxActive: z.number().int().positive(),
  nameMinLength: z.number().int().positive(),
  nameMaxLength: z.number().int().positive(),
  /**
   * Arşivli bir adla çakışmada ne olacağı.
   *
   * `block`: reddet. `suggest_reactivate`: reddet ama kullanıcıyı YENİDEN AKTİFLEŞTİRMEYE
   * yönlendir — kart *"engellenir VEYA açık yeniden aktifleştirmeye yönlendirilir"* diyor
   * ve ikisi arasındaki fark bir ürün kararı, bir kod dallanması değil.
   */
  archivedNameConflict: z.enum(["block", "suggest_reactivate"]),
  /** Liste varsayılanı: arşivliler gösterilsin mi (kartın "filtreleme davranışı"). */
  includeArchivedByDefault: z.boolean(),
});
export type DepartmentConfig = z.infer<typeof departmentConfigSchema>;

/**
 * Ad NORMALİZASYONU (kart: *"ad normalize edilir"*).
 *
 * ⚠ Türkçe küçültme: `toLowerCase()` "İK"yı `i̇k` yapar ve "ik" ile çakışmaz — iki
 * organizasyonun aynı departmanı iki ayrı satır olarak yaşardı. Aradaki boşluklar da
 * teke iniyor: *"İnsan  Kaynakları"* ile *"İnsan Kaynakları"* aynı departmandır ve
 * ikisinin yan yana durması listeyi okunamaz kılardı.
 *
 * Sonuç YALNIZ çakışma karşılaştırmasında kullanılıyor; kullanıcının yazdığı ad olduğu
 * gibi saklanıyor — normalize edilmiş hâli göstermek, kullanıcının büyük harflerini
 * elinden almak olurdu.
 */
export function normalizeDepartmentName(name: string): string {
  return name.trim().replace(/\s+/g, " ").toLocaleLowerCase("tr");
}

const adSemasi = z
  .string({ error: "Departman adı zorunludur" })
  .trim()
  .min(1, "Departman adı zorunludur");

export const createDepartmentSchema = z.object({
  name: adSemasi,
  description: z.string().max(500, "Açıklama en fazla 500 karakter olabilir").nullish(),
});
export type CreateDepartmentInput = z.infer<typeof createDepartmentSchema>;

/**
 * Düzenleme.
 *
 * `version` ZORUNLU: iki yönetici aynı departmanı aynı anda yeniden adlandırdığında
 * ikincisi birincinin değişikliğini sessizce ezmesin (kart: *"stale update ... test
 * edilir"*). Kontrol `UPDATE`'in koşulunda — "önce oku, sürüm tutuyorsa yaz" deseni iki
 * eşzamanlı isteği de okumadan geçirirdi.
 */
export const updateDepartmentSchema = z.object({
  name: adSemasi.optional(),
  description: z.string().max(500, "Açıklama en fazla 500 karakter olabilir").nullish(),
  version: z.number().int().nonnegative({ error: "Sürüm bilgisi eksik" }),
});
export type UpdateDepartmentInput = z.infer<typeof updateDepartmentSchema>;

export const departmentSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  /** `null` ise departman AKTİF. Zaman damgası: "ne zaman arşivlendi?" denetimde sorulur. */
  archivedAt: z.string().nullable(),
  version: z.number().int().nonnegative(),
  createdAt: z.string(),
});
export type Department = z.infer<typeof departmentSchema>;

/**
 * Arşivlemenin ETKİ ÖZETİ (kart: *"arşivleme öncesi bağlı üyelikler, sözleşme
 * sorumlulukları, workflow görevleri ve diğer aktif referanslar güvenli etki özetiyle
 * gösterilir"*).
 *
 * ⚠ Sayılar, İSİMLER değil. Etki özeti bir yönetim kararının girdisi; kimlerin bağlı
 * olduğunu görmek ayrı bir yüzey (CLOS-189, departman filtresi) ve ayrı bir yetki.
 *
 * Sözleşme ve workflow alanları BUGÜN YOK: o domainler henüz kurulmadı (EP06+). Sıfır
 * dolu alanlar koymak, hiçbir kodun beslemediği bir söz vermek olurdu (ADR-0014); alanlar
 * kendi kartlarıyla eklenecek ve bu şema o gün büyüyecek.
 */
export const departmentImpactSchema = z.object({
  /** Bu departmana bağlı ÜYELİK sayısı — aktif ve askıdakiler. */
  memberships: z.number().int().nonnegative(),
  /**
   * ÇALIŞAN EKİP BAĞLARI (CLOS-1225 — Departmanlar Kanonu §24.2).
   *
   * ⚠ Şema BÜYÜDÜ çünkü domain kuruldu. Eski yorum *"sözleşme ve workflow alanları BUGÜN
   * YOK: o domainler henüz kurulmadı; alanlar kendi kartlarıyla eklenecek ve bu şema o gün
   * büyüyecek"* diyordu. O gün bu.
   *
   * ⚠ §24.2 arşiv öncesi taranacak bağımlılıkları sayıyor: aktif katılımlar, sözleşmeye
   * özel üyelikler, workflow seçicileri. Yalnız üyelik sayısını gösteren bir özet, bağlı
   * bir sözleşme varken bile "kimseyi etkilemiyor" izlenimi verirdi.
   */
  activeParticipations: z.number().int().nonnegative(),
  scopedMembers: z.number().int().nonnegative(),
  workflowTargets: z.number().int().nonnegative(),
  /** Legacy "İlgili departman" etiketi (CLOS-1227'de emekliye ayrılıyor). */
  legacyResponsibilities: z.number().int().nonnegative(),
  /**
   * ⚠ ARŞİVLEMEYİ ENGELLEYEN bağımlılık var mı (§24.3: *"active dependencies çözülmeden
   * tamamlanmamalıdır"*)? Sayıları istemcinin yorumlamasına bırakmıyoruz: hangi sayının
   * engel olduğu bir ÜRÜN kararı ve iki istemci iki farklı yorum yapabilirdi.
   */
  archiveBlocked: z.boolean(),
});
export type DepartmentImpact = z.infer<typeof departmentImpactSchema>;

/**
 * Üyeliği bir departmana BAĞLAMA girdisi (CLOS-189 `[051][S05]`).
 *
 * ⚠ Şema burada, `department.ts`'te, ama KOMUT CLOS-189'un: bu dosya departman kavramının
 * sözleşmesi ve atamanın girdisi de o kavrama ait. Kartların böldüğü şey kod dosyası değil,
 * hangi kartın hangi komutu YAZDIĞI.
 */
export const assignDepartmentSchema = z.object({
  departmentId: z.uuid("Geçerli bir departman seçilmelidir"),
  /**
   * ANA DEPARTMAN (CLOS-1218 — Departmanlar Kanonu §5.2).
   *
   * ⚠ OPSİYONEL ve varsayılanı `false`. Kanon *"department kullanımı opsiyonel ise primary
   * zorunlu değildir"* diyor: zorunlu kılsaydık, departmanı yalnız etiket olarak kullanan
   * bir organizasyon her atamada anlamsız bir seçim yapmak zorunda kalırdı.
   *
   * ⚠ Ana olmak YETKİ ÜRETMEZ (§5.3). Bu alan yalnız "hangi ekip onun asıl ekibi"
   * sorusunu cevaplıyor.
   */
  isPrimary: z.boolean().optional(),
});
export type AssignDepartmentInput = z.infer<typeof assignDepartmentSchema>;
